import { Link } from "@tanstack/react-router";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SupportedLang } from "@/lib/translations.functions";

const COPY: Record<SupportedLang, { title: string; body: string; action: string }> = {
  en: {
    title: "Help the village speak your language",
    body: "Spotted a phrase that feels off? Tell us. Your correction makes the next discovery better for every Smartasaurus who speaks your language.",
    action: "Share a correction",
  },
  es: {
    title: "Ayuda a la comunidad a hablar tu idioma",
    body: "¿Has visto alguna frase que no suena natural? Cuéntanoslo. Tu corrección mejorará la próxima historia para todos los Smartasaurus que hablan tu idioma.",
    action: "Enviar una corrección",
  },
  sv: {
    title: "Hjälp oss tala ditt språk bättre",
    body: "Har du hittat en formulering som inte känns naturlig? Berätta för oss. Din rättelse gör nästa upptäckt bättre för alla Smartasaurier som talar ditt språk.",
    action: "Skicka en rättelse",
  },
  zh: {
    title: "帮恐龙村把你的语言说得更地道",
    body: "发现哪句话读起来不自然？请告诉我们。你的指正会让每一位使用这种语言的聪明龙都能读到更好的探索故事。",
    action: "提交修改建议",
  },
  fr: {
    title: "Aidez le village à mieux parler votre langue",
    body: "Une phrase vous semble peu naturelle ? Dites-le-nous. Votre correction améliorera les prochaines découvertes pour tous les Smartasaurus qui parlent votre langue.",
    action: "Signaler une correction",
  },
  pt: {
    title: "Ajude a comunidade a falar melhor o seu idioma",
    body: "Encontrou alguma frase que não soa natural? Conte para a gente. Sua correção melhora as próximas descobertas para todos os Smartasaurus que falam o seu idioma.",
    action: "Enviar uma correção",
  },
  de: {
    title: "Hilf dem Rudel, deine Sprache besser zu sprechen",
    body: "Klingt eine Formulierung unnatürlich? Sag uns Bescheid. Deine Korrektur macht die nächste Entdeckung für alle Smartasaurier in deiner Sprache besser.",
    action: "Korrektur senden",
  },
  hi: {
    title: "हमारे समुदाय को अपनी भाषा बेहतर बोलना सिखाएँ",
    body: "कोई वाक्य अस्वाभाविक लगा? हमें बताएँ। आपका सुधार आपकी भाषा पढ़ने वाले हर Smartasaurus के लिए अगली खोज को बेहतर बनाएगा।",
    action: "सुधार भेजें",
  },
  ru: {
    title: "Помогите сообществу лучше говорить на вашем языке",
    body: "Заметили неестественную фразу? Сообщите нам. Ваша правка сделает следующие открытия лучше для всех, кто читает на вашем языке.",
    action: "Предложить исправление",
  },
  ar: {
    title: "ساعد مجتمعنا على إتقان لغتك",
    body: "هل وجدت عبارة غير طبيعية؟ أخبرنا. تصحيحك يجعل الاكتشافات القادمة أفضل لكل من يقرأ بلغتك.",
    action: "إرسال تصحيح",
  },
};

export function LanguageFeedbackBanner({ lang }: { lang: SupportedLang }) {
  if (lang === "en") return null;
  const copy = COPY[lang];

  return (
    <aside
      data-no-translate
      className="mt-8 flex flex-col gap-4 border-y border-primary/25 py-5 sm:flex-row sm:items-center"
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div>
          <h2 className="font-display text-lg uppercase leading-tight text-foreground">
            {copy.title}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>
        </div>
      </div>
      <Button asChild variant="outline" size="sm" className="self-start sm:shrink-0 sm:self-auto">
        <Link to="/contact" search={{ topic: "feedback" } as never}>
          {copy.action}
        </Link>
      </Button>
    </aside>
  );
}